import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";

const empty = { title: "", excerpt: "", content: "", cover_image: "", category: "immobiliare", author: "Edilstudio", published: true, meta_title: "", meta_description: "" };
const CATEGORIES = [
  { slug: "amministrazioni", label: "Amministrazioni condominiali" },
  { slug: "immobiliare", label: "Immobiliare" },
  { slug: "ristrutturazioni", label: "Ristrutturazioni" },
  { slug: "affitto", label: "Affitto" },
];
const field = "w-full px-4 py-2.5 rounded-xl border border-[#DCE6EF] bg-white focus:outline-none focus:ring-2 focus:ring-[#2472C8]/40";

export default function AdminBlog() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: posts = [] } = useQuery({ queryKey: ["admin-posts"], queryFn: async () => (await api.get("/admin/posts")).data });

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    await api.delete(`/admin/posts/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-posts"] });
    toast.success("Articolo eliminato");
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display text-3xl font-bold text-[#1B2A41]">Blog</h1>
        <button onClick={() => setEditing({ ...empty })} className="bg-[#2472C8] hover:bg-[#1E5FA6] text-white font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 transition-colors" data-testid="add-post-btn"><Plus className="w-5 h-5" /> Nuovo articolo</button>
      </div>

      <div className="mt-6 space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#DCE6EF] p-4 flex items-center gap-4" data-testid={`admin-post-${p.id}`}>
            {p.cover_image ? <img src={mediaUrl(p.cover_image)} alt="" className="w-20 h-16 rounded-lg object-cover shrink-0" /> : <div className="w-20 h-16 rounded-lg bg-[#E3ECF3] shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><h3 className="font-heading font-semibold text-[#1B2A41] truncate">{p.title}</h3><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.published ? "bg-[#166534] text-white" : "bg-[#566476] text-white"}`}>{p.published ? "PUBBLICO" : "BOZZA"}</span></div>
              <p className="text-sm text-[#566476] line-clamp-1 mt-1">{p.excerpt}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(p)} className="p-2 bg-[#1B2A41] text-white rounded-lg" data-testid={`edit-post-${p.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} className="p-2 border border-[#DCE6EF] text-[#7A1F1F] rounded-lg hover:bg-red-50" data-testid={`delete-post-${p.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {!posts.length && <div className="bg-white rounded-2xl border border-dashed border-[#CBD7E2] p-12 text-center text-[#8695A6]">Nessun articolo. Scrivi il tuo primo post per la SEO locale.</div>}
      </div>

      {editing && <PostEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["admin-posts"] }); qc.invalidateQueries({ queryKey: ["stats"] }); }} />}
    </div>
  );
}

function PostEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...empty, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Il titolo è obbligatorio");
    setSaving(true);
    try {
      if (f.id) await api.put(`/admin/posts/${f.id}`, f);
      else await api.post("/admin/posts", f);
      toast.success("Articolo salvato");
      onSaved();
    } catch (e) { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" data-testid="post-editor">
      <div className="bg-[#F7FAFC] w-full sm:max-w-3xl sm:rounded-2xl min-h-screen sm:min-h-0 my-0 sm:my-8">
        <div className="sticky top-0 bg-[#1B2A41] text-white px-6 py-4 flex items-center justify-between sm:rounded-t-2xl z-10">
          <h2 className="font-display text-xl font-bold">{f.id ? "Modifica articolo" : "Nuovo articolo"}</h2>
          <button onClick={onClose} data-testid="post-editor-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div><label className="block text-sm font-medium mb-2">Titolo *</label><input value={f.title} onChange={set("title")} className={field} data-testid="post-title" /></div>
          <div><label className="block text-sm font-medium mb-2">Estratto</label><textarea value={f.excerpt} onChange={set("excerpt")} rows={2} className={field} data-testid="post-excerpt" /></div>
          <ImageUploader value={f.cover_image ? [{ path: f.cover_image, url: f.cover_image }] : []} onChange={(v) => setF({ ...f, cover_image: v[v.length - 1]?.url || "" })} label="Immagine di copertina" testid="post-cover" />
          <div><label className="block text-sm font-medium mb-2">Contenuto (testo o HTML)</label><textarea value={f.content} onChange={set("content")} rows={12} className={`${field} font-mono text-sm`} data-testid="post-content" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-2">Categoria</label>
              <select value={f.category || "immobiliare"} onChange={set("category")} className={field} data-testid="post-category">
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-2">Autore</label><input value={f.author} onChange={set("author")} className={field} /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="accent-[#2472C8]" data-testid="post-published" /> Pubblicato</label>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[#DCE6EF]">
            <div><label className="block text-sm font-medium mb-2 mt-2">Meta title (SEO)</label><input value={f.meta_title} onChange={set("meta_title")} className={field} /></div>
            <div><label className="block text-sm font-medium mb-2 mt-2">Meta description (SEO)</label><input value={f.meta_description} onChange={set("meta_description")} className={field} /></div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-[#F7FAFC] border-t border-[#DCE6EF] px-6 py-4 flex justify-end gap-3 sm:rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#DCE6EF] font-semibold text-[#1B2A41]">Annulla</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full bg-[#2472C8] hover:bg-[#1E5FA6] text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="save-post-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva articolo</button>
        </div>
      </div>
    </div>
  );
}
